import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import thunk from 'redux-thunk';
import storage from 'redux-persist/lib/storage';
import reducers from './reducer.config';
import { encryptTransform } from 'redux-persist-transform-encrypt';

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/**
 * Persisted auth state lives in localStorage, not sessionStorage.
 *
 * sessionStorage is cleared the moment the tab — or, for an installed PWA, the
 * app — is closed. Pantry staff open this app many times a day on a phone, and
 * were being asked to sign in again every single time. localStorage survives
 * app restarts and device reboots, which is the behaviour the JWT's own expiry
 * is there to bound.
 *
 * Nothing else about authentication changes: same endpoint, same JWT, same
 * shape. Only where the already-encrypted blob is written.
 */
const storageEngine = storage;

/**
 * The key that encrypts persisted state. REQUIRED — there is no fallback.
 *
 * This used to fall back to a literal compiled into the bundle, which is not a
 * secret in any meaningful sense: anything shipped to a browser is readable by
 * whoever holds the device. Keeping a hard-coded constant only created the
 * impression of protection while guaranteeing every deployment shared one key.
 *
 * Failing loudly is the honest behaviour. A missing key is a deployment
 * mistake, and a build that silently persists under a known constant hides it.
 * Set REACT_APP_ENCRYPTION_KEY at BUILD time (CRA inlines it) before deploying.
 */
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

if (!ENCRYPTION_KEY) {
    throw new Error(
        'REACT_APP_ENCRYPTION_KEY is not set. Persisted state cannot be encrypted. ' +
        'Set it in the environment before building (create-react-app inlines it at build time).'
    );
}

const persistConfig = {
    key: 'root',
    storage: storageEngine,
    blacklist: [
        'sidebarReducer',
        'userReducer',
        'loaderReducer',
        'partnerReducer',
        'siteReducer',
        'locationReducer',
        'floorReducer',
        'pantryReducer',
        'machineReducer'
    ],
    transforms: [
        encryptTransform({
            secretKey: ENCRYPTION_KEY,
        }),
    ]
};

const persistedReducer = persistReducer(persistConfig, reducers);

/**
 * redux-logger and the Redux DevTools bridge are DEVELOPMENT ONLY.
 *
 * Both were enabled unconditionally, so production shipped a console trace of
 * every dispatched action — including the login payload carrying the JWT — and
 * left application state open to inspection through the DevTools extension on
 * any customer's device.
 */
const middleware = [thunk];

// The condition is written out INLINE rather than reusing IS_PRODUCTION above,
// and that is not a style choice. webpack substitutes process.env.NODE_ENV at
// parse time and can then drop the dead branch along with its require() — but
// only when it can see the comparison literally. Behind a derived constant the
// branch still looks live to the module-graph builder, redux-logger is pulled
// in regardless, and the production bundle ships a logger that never runs.
// Verified by grepping the built artifact both ways.
//
// eslint-disable-next-line no-undef
if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line global-require
    middleware.unshift(require('redux-logger').default);
}

export const store = configureStore({
    reducer: persistedReducer,
    devTools: !IS_PRODUCTION,
    middleware
});

export const persistor = persistStore(store);

/**
 * Clear persisted authentication.
 *
 * Resetting the reducer alone is not enough: redux-persist keeps its own copy
 * in localStorage, and now that the copy outlives the tab, a signed-out user's
 * token would otherwise sit on the device until something happened to overwrite
 * it. purge() removes the stored blob; flush() makes sure the write lands
 * before the caller navigates away.
 */
export const purgePersistedState = async () => {
    try {
        await persistor.flush();
    } catch (e) {
        // A flush failure must not block sign-out.
    }
    return persistor.purge();
};
